import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { clearRequestCache } from '@/lib/supabaseUtils';
import { clearTenantIdCache } from '@/utils/tenantUtils';
// SUB-4.2. This service memoises the resolved account and caches rows under it.
// It heals itself within seconds, but the scope changes HERE, so it is told here
// too: a stale pipeline board is the visible half of the same defect.
import { pipelinePaginationService } from '@/services/pipelinePaginationService';
import { useAuth } from './AuthContext';
import { getDomainTenant, getDefaultTenant } from '@/services/domainService';
import { holdsAgencyAdminRole } from '@/lib/tenantPicker';
import {
  classifyScope,
  scopeNoticeFor,
  type ScopeNotice,
  type ScopeStatus,
} from '@/lib/tenantScope';
import {
  UNRESOLVED_MERGED_SCOPE,
  mergedScopeKey,
  parseMergedScopeStatus,
  scopeReachKey,
  type MergedScopeStatus,
} from '@/lib/mergedScope';
import { clearScopeCaches } from '@/lib/scopeCaches';
import { useQueryClient } from '@tanstack/react-query';
import { ScopeChangeNotice } from '@/components/layout/ScopeChangeNotice';

interface Tenant {
  tenant_id: string;
  company_name: string;
  contact_name: string;
  contact_phone?: string | null;
  contact_email: string;
  created_at: string;
  updated_at: string;
  // SUB-2.10 · The account's parent, NULL for standalone and parent accounts.
  // Already returned by every `tenants` select below (they are all `select('*')`)
  // since migration 20260812e added the column; this only stops the type discarding
  // it. Optional, so no existing construction of a Tenant object has to change.
  parent_tenant_id?: string | null;
}

interface TenantContextType {
  currentTenant: Tenant | null;
  userTenants: Tenant[];
  /**
   * SUB-2.7 · FR12. True when the user holds the Agency Admin role in any account
   * AND IS NOT A SUPERADMIN.
   *
   * ⚠️ The second half is not a detail. The superadmin branch of `fetchUserTenants`
   * returns false without querying, on the reasoning that a superadmin's picker is
   * unconditional anyway — so a superadmin who genuinely holds Agency Admin reads
   * false. Both current consumers pair this with `hasSystemOverride` and the
   * difference is invisible; a consumer that reads this field alone will be wrong,
   * and no test will catch it. The name says more than the value does.
   *
   * Read from the same `user_roles` fetch that builds `userTenants`, so the picker
   * gains no round trip. Presentation only: it decides what renders, never what is
   * permitted.
   */
  isAgencyAdmin: boolean;
  /**
   * SUB-2.90 · F-82. The SAME superadmin answer that decided `isAgencyAdmin` and
   * `userTenants`, published so a consumer cannot pair one with the other.
   *
   * ⚠️ **READ THIS INSTEAD OF `usePermissions().hasPermission('system.override')`
   * WHENEVER YOU ALSO READ `isAgencyAdmin`.** The two are different questions with
   * the same name and they disagree on real users. `fetchUserTenants` calls
   * `checkSystemOverridePermission()`, which finds Superadmin through `user_roles`.
   * `hasPermission` resolves permissions from the legacy `users.role_id`, and
   * `system.override` sits on exactly one role (42, Superadmin), so a user holding
   * Superadmin only in `user_roles` reads TRUE from the first and FALSE from the
   * second.
   *
   * That gap deleted the account picker. The superadmin branch of
   * `fetchUserTenants` sets `isAgencyAdmin` false on the documented reasoning that
   * "both current consumers mask that with `hasSystemOverride`" — which is only
   * safe while it is THIS flag. Both consumers were reading the other one, so the
   * viewer came out `{hasSystemOverride: false, isAgencyAdmin: false}` and
   * `canSeeTenantPicker` refused a user who was both a superadmin and an Agency
   * Admin. Measured on 2026-08-17: `merged_scope_status()` said `available: true`
   * and the UI offered no way in, on the picker and on `/app/sub-accounts` alike.
   */
  hasSystemOverride: boolean;
  /**
   * SUB-2.7 · FR12. Whether `isAgencyAdmin` is an ANSWER rather than a default.
   *
   * ⚠️ This exists because `isAgencyAdmin` has three states and only two values.
   * It starts false, it is false for a user who is not one, and it was also false
   * when the `roles(name)` embed failed to resolve and the fallback query dropped
   * the marker. `isLoading` still went false on that path, so a consumer reading
   * "loading is done, and the answer is no" got a confident denial derived from a
   * failed read — and since SUB-2.11 that no longer merely hides a picker, it
   * hard-redirects a real Agency Admin off `/app/sub-accounts`.
   *
   * False means "not known", not "not an Agency Admin". Gates must treat it as
   * unresolved, exactly as they treat `isLoading`.
   */
  isAgencyAdminResolved: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
  returnToHomeTenant: () => Promise<void>;
  getCurrentTenantId: () => Promise<string | null>;
  /**
   * SUB-3.12 · NFR8. Whether the account being rendered is still the account the
   * server resolves to. SUB-4.10 · and whether the SET of accounts being served
   * is still the set this session asked for.
   *
   * ⚠️ `'unknown'` IS NOT `'ok'`. It means the resolver could not be reached, or
   * nothing has rendered yet. A consumer that treats it as agreement makes
   * exactly the claim NFR8 forbids. See `lib/tenantScope.ts`.
   *
   * ⚠️ `'degraded'` IS NOT `'ok'` EITHER, and it is the one an existing consumer
   * is most likely to miss, because the page still works: the account on screen
   * is right and only the lists are short.
   *
   * The intended consumer is an empty state: a list with no rows under anything
   * other than `'ok'` must not render "nothing here", because the emptiness may
   * be the scope rather than the data. `emptyStateIsTrustworthy()` is that test.
   */
  scopeStatus: ScopeStatus;
  /**
   * Re-reads `get_current_user_tenant_id()` UNCACHED and reclassifies. Called on
   * tab focus, on visibility change and on a timer; exposed so a surface that
   * knows it is about to show something scope-dependent can ask first.
   *
   * SUB-4.10. The optional argument is the merged reading to classify against,
   * which the poll passes in so one poll makes one classification. Called with
   * no argument it uses the last reading this provider held, so an existing
   * caller keeps working and no caller has to know about merged scope to ask.
   */
  revalidateScope: (merged?: MergedScopeStatus | null) => Promise<ScopeStatus>;
  /**
   * SUB-4.1 · FR31, FR32, FR43, FR71, FR33. Whether the merged "All Accounts"
   * scope may be offered, whether this session is in it, and which single
   * account everything outside it resolves to.
   *
   * Read from `merged_scope_status()` in one call, re-read on the same focus,
   * visibility and timer schedule as `revalidateScope`, so a revoked grant or a
   * detached last sub-account removes the entry rather than leaving it offering
   * something the server will refuse.
   *
   * ⚠️ `available: false` WITH `resolved: false` IS NOT A DENIAL. See
   * `lib/mergedScope.ts`. Use `offersMergedScope()` / `isMergedScope()` rather
   * than reading the booleans directly.
   */
  mergedScope: MergedScopeStatus;
  /**
   * SUB-4.1 · FR31. Enter merged scope. Anchors server-side at the caller's home
   * account, so it works from inside a sub-account and never anchors there.
   *
   * Throws when the server refuses. There is no client-side pre-check beyond
   * `mergedScope.available`, which is that same refusal asked in advance.
   */
  enterMergedScope: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addTenant: (tenant: Tenant) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Global cache for tenant data to prevent duplicate requests
const tenantCache = new Map<string, {
  tenant: Tenant | null;
  timestamp: number;
  promise?: Promise<any>;
}>();

// Global cache for tenant ID to prevent repeated database calls
const tenantIdCache = new Map<string, {
  tenantId: string | null;
  timestamp: number;
  promise?: Promise<string | null>;
}>();

// Cache expiration times
const TENANT_CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes for tenant data
const TENANT_ID_CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes for tenant ID

// Exponential backoff for retrying failed requests
const retryWithBackoff = async function <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      const isLastAttempt = attempt === maxRetries - 1;
      const is522Error = error?.status === 522 || error?.code === 522;
      
      if (isLastAttempt || !is522Error) {
        throw error;
      }
      
      // Exponential backoff for 522 errors
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`[TenantContext] 522 error on attempt ${attempt + 1}, retrying in ${delay}ms`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  
  throw new Error('Max retries exceeded');
};

// Deduplicated tenant request function
const deduplicatedTenantRequest = async (
  cacheKey: string,
  requestFn: () => Promise<any>
): Promise<any> => {
  const cached = tenantCache.get(cacheKey);
  const now = Date.now();

  // Return cached data if it's still valid
  if (cached && now - cached.timestamp < TENANT_CACHE_EXPIRY) {
    return cached.tenant;
  }

  // If there's an ongoing request, wait for it
  if (cached?.promise) {
    return await cached.promise;
  }

  // Make the request and cache the promise
  const promise = requestFn();
  tenantCache.set(cacheKey, {
    tenant: null,
    timestamp: now,
    promise
  });

  try {
    const result = await promise;
    tenantCache.set(cacheKey, {
      tenant: result,
      timestamp: now
    });
    return result;
  } catch (error) {
    // Remove failed request from cache
    tenantCache.delete(cacheKey);
    throw error;
  }
};

// Deduplicated tenant ID request function
const deduplicatedTenantIdRequest = async (
  cacheKey: string,
  requestFn: () => Promise<string | null>
): Promise<string | null> => {
  const cached = tenantIdCache.get(cacheKey);
  const now = Date.now();

  // Return cached data if it's still valid
  if (cached && now - cached.timestamp < TENANT_ID_CACHE_EXPIRY) {
    return cached.tenantId;
  }

  // If there's an ongoing request, wait for it
  if (cached?.promise) {
    return await cached.promise;
  }

  // Make the request and cache the promise
  const promise = requestFn();
  tenantIdCache.set(cacheKey, {
    tenantId: null,
    timestamp: now,
    promise
  });

  try {
    const result = await promise;
    tenantIdCache.set(cacheKey, {
      tenantId: result,
      timestamp: now
    });
    return result;
  } catch (error) {
    // Remove failed request from cache
    tenantIdCache.delete(cacheKey);
    throw error;
  }
};

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user, authLoading } = useAuth();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [userTenants, setUserTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userHasSystemOverride, setUserHasSystemOverride] = useState(false);
  // SUB-2.7 · FR12. Set from the user_roles rows fetched below, for every user.
  const [userIsAgencyAdmin, setUserIsAgencyAdmin] = useState(false);
  // See isAgencyAdminResolved on TenantContextType. Set true only where the marker
  // was actually read, never where it was defaulted.
  const [userIsAgencyAdminResolved, setUserIsAgencyAdminResolved] = useState(false);
  // SUB-3.12 · NFR8. Starts 'unknown', which is the honest value before anything
  // has been asked, and is deliberately not 'ok'.
  const [scopeStatus, setScopeStatus] = useState<ScopeStatus>('unknown');
  const [scopeNotice, setScopeNotice] = useState<ScopeNotice | null>(null);
  /**
   * RBAC-4.3. Who is signed in NOW, read after each await in `revalidateScope`. A poll in flight when
   * the person is signed out (their access was removed) answers after the sign-out; without this its
   * "could not be determined" notice lands over the login page.
   */
  const currentUserIdRef = useRef<string | null>(null);
  currentUserIdRef.current = user?.id ?? null;
  // SUB-4.1 · FR71. Starts unresolved, which is deliberately not "no merged
  // scope": see the `resolved` field's comment in lib/mergedScope.ts.
  const [mergedScope, setMergedScope] = useState<MergedScopeStatus>(UNRESOLVED_MERGED_SCOPE);

  const abortControllerRef = useRef<AbortController>(new AbortController());
  // What is on screen, readable from a callback without re-subscribing every
  // effect to `currentTenant`. The notice compares against what the USER SEES,
  // not against whatever the last render closed over.
  const currentTenantRef = useRef<Tenant | null>(null);
  currentTenantRef.current = currentTenant;

  const queryClient = useQueryClient();
  // SUB-4.7 · FR74, AC2. The reach the caches in this tree currently hold data
  // for. A ref, not state: this is read and written inside the revalidation
  // callback and must never itself cause a render.
  const scopeReachRef = useRef<string>(scopeReachKey(UNRESOLVED_MERGED_SCOPE));
  /**
   * SUB-4.10 · NFR8. The last merged reading, so `revalidateScope` can classify
   * against it when it is called without one, and the accounts that were in
   * reach when this page last AGREED with the server.
   *
   * Refs rather than state, for the same reason `scopeReachRef` is one: they are
   * read and written inside the polling callback and must never cause a render.
   * `reachBaselineRef` starts null, which is "this tab has not learned where it
   * is yet" — deliberately not the empty array, which would read as a set that
   * every subsequent reading is a superset of.
   */
  const mergedReadingRef = useRef<MergedScopeStatus>(UNRESOLVED_MERGED_SCOPE);
  const reachBaselineRef = useRef<readonly string[] | null>(null);

  /**
   * SUB-4.7 · FR74, AC2. Empty every cache that is keyed by user rather than by
   * scope, plus React Query.
   *
   * ⚠️ THE FOUR MODULE-LEVEL MAPS ARE NOT ENOUGH, AND THE PERMISSION CACHE IS
   * WHY THE REGISTRY EXISTS. Its key is
   * `user-permissions-${user.id}-${currentTenant.tenant_id}`, which LOOKS
   * scope-safe because it carries a tenant id. In merged scope
   * `get_current_user_tenant_id()` returns the ANCHOR, which for a tenant user
   * is their own parent account, so that string is IDENTICAL in merged scope and
   * in single-account scope on the parent. A per-tenant key is not a per-scope
   * key. `lib/scopeCaches.ts` has the reasoning and the direction of imports.
   *
   * ⚠️ `queryClient.clear()` RATHER THAN `invalidateQueries()`. Invalidation
   * marks entries stale and keeps serving them while the refetch is in flight,
   * which is the merged list rendering another scope's rows for as long as the
   * network takes. There is no query in this app whose result is scope
   * independent, so there is nothing worth keeping.
   */
  const dropScopedCaches = useCallback((reason: string) => {
    tenantCache.clear();
    tenantIdCache.clear();
    clearTenantIdCache();
    clearRequestCache();
    pipelinePaginationService.resetScopeCaches();
    const cleared = clearScopeCaches();
    queryClient.clear();
    console.info(
      `[TenantContext] scope caches dropped (${reason}); registry cleared: ${
        cleared.length ? cleared.join(', ') : 'none registered'
      }`,
    );
  }, [queryClient]);

  // Clear cache when user changes
  useEffect(() => {
    tenantCache.clear();
    tenantIdCache.clear();
    clearTenantIdCache(); // Clear tenant ID cache from tenantUtils.ts
    clearRequestCache(); // Clear request cache when user changes
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
  }, [user?.id]);

  const getCurrentTenantId = async (): Promise<string | null> => {
    if (!user) return null;

    const cacheKey = `tenant-id-${user.id}`;
    
    return await deduplicatedTenantIdRequest(cacheKey, async () => {
      return await retryWithBackoff(async () => {
        const { data, error } = await supabase.rpc('get_current_user_tenant_id');
        
        if (error) {
          console.error('[TenantContext] Error getting current tenant ID:', error);
          return null;
        }

        return data;
      });
    });
  };

  /**
   * SUB-3.12 · NFR8. Ask the server what account this session is actually in,
   * and say so when the answer stops matching the page.
   *
   * ⚠️ IT DELIBERATELY BYPASSES BOTH CACHES. `getCurrentTenantId` memoises for
   * ten minutes and `fetchCurrentTenant` for five, which is the whole problem
   * this function exists to detect: RLS re-resolves per query while the React
   * tree does not, so for up to ten minutes the header can name one account
   * while every list is being served another's rows. Reading through the cache
   * here would return the stale value and report agreement with itself.
   *
   * ⚠️ A FAILED CALL IS NOT AN ANSWER. On error the status goes to 'unknown' and
   * NO notice is raised. Raising one on a flaky request would train the user to
   * dismiss the notice that matters.
   */
  const revalidateScope = useCallback(async (
    merged?: MergedScopeStatus | null,
  ): Promise<ScopeStatus> => {
    if (!user) {
      setScopeStatus('unknown');
      setScopeNotice(null);
      return 'unknown';
    }

    let resolvedTenantId: string | null = null;
    let resolverReachable = true;

    try {
      const { data, error } = await supabase.rpc('get_current_user_tenant_id');
      if (error) {
        resolverReachable = false;
      } else {
        resolvedTenantId = (data as string | null) ?? null;
      }
    } catch {
      resolverReachable = false;
    }

    /**
     * SUB-4.10 · NFR8 for a scope that is a SET.
     *
     * ⚠️ THE MERGED READING IS PASSED IN, NOT FETCHED HERE. `ask()` reads
     * `merged_scope_status()` first and hands the answer over, so one poll makes
     * one classification. Two classifiers each setting `scopeNotice` is a race,
     * and its loser is the notice never appearing — which is the exact failure
     * this story exists to prevent, arrived at by trying to prevent it.
     *
     * An explicit `null`, and the no-argument call a consumer may make, both
     * fall back to the last reading this provider held. A caller that knows
     * nothing about merged scope gets SUB-3.12's behaviour and no notice it
     * cannot explain.
     */
    // RBAC-4.3: the answer belongs to a session that has since ended.
    if (currentUserIdRef.current !== user.id) return 'unknown';

    const reading = merged ?? mergedReadingRef.current;
    const mergedIsAnswer = reading.resolved;
    const resolvedTenantIds = mergedIsAnswer ? reading.tenantIds : null;

    const displayed = currentTenantRef.current;
    const status = classifyScope({
      displayedTenantId: displayed?.tenant_id ?? null,
      resolvedTenantId,
      resolverReachable,
      requestedMergedScope: mergedIsAnswer && reading.requestedScopeMode !== null
        ? reading.requestedScopeMode === 'subtree'
        : undefined,
      resolvedTenantIds,
      previousTenantIds: reachBaselineRef.current,
    });

    /**
     * ⚠️ THE BASELINE IS FROZEN WHILE A DEGRADATION IS OUTSTANDING. Adopting the
     * narrowed set as the new baseline is how a blocking notice clears itself
     * sixty seconds after it appears: the next poll compares the shrunken set
     * against itself, finds agreement, and the page goes back to quietly showing
     * a subset. The notice is dismissed by RELOADING, which is what its one
     * button does, and a reload rebuilds this ref from nothing.
     */
    if (status !== 'degraded' && resolvedTenantIds) {
      reachBaselineRef.current = resolvedTenantIds;
    }

    setScopeStatus(status);

    if (status === 'ok' || status === 'unknown') {
      setScopeNotice(null);
      return status;
    }

    // The account they are being moved TO. Looked up rather than guessed: it is
    // usually their home account, but "usually" is not a name to put on screen.
    let resolvedName: string | null = null;
    if (resolvedTenantId) {
      const known = userTenants.find(t => t.tenant_id === resolvedTenantId);
      if (known) {
        resolvedName = known.company_name;
      } else {
        const { data } = await supabase
          .from('tenants')
          .select('company_name')
          .eq('tenant_id', resolvedTenantId)
          .maybeSingle();
        resolvedName = data?.company_name ?? null;
      }
    }

    if (currentUserIdRef.current !== user.id) return 'unknown';
    setScopeNotice(
      scopeNoticeFor(status, displayed?.company_name ?? null, resolvedName, {
        accountsNow: resolvedTenantIds?.length ?? 0,
        accountsBefore: reachBaselineRef.current?.length ?? null,
      }),
    );
    return status;
  }, [user?.id, userTenants]);

  /**
   * SUB-4.1 · FR71, FR43. Ask the server whether All Accounts may be offered and
   * whether this session is in it.
   *
   * ⚠️ ONE CALL, AND NO CLIENT-SIDE DERIVATION. Availability is
   * `may_enter_merged_scope()` evaluated at the anchor `set_scope_mode` would
   * actually use, so the picker offers the entry exactly when choosing it would
   * succeed. Counting sub-accounts in `userTenants` instead would be a second
   * spelling of FR71 keyed on rows this component happened to fetch, and it
   * would be wrong for an Agency Admin whose grant over a client has been
   * revoked while the account is still in their list.
   *
   * ⚠️ A FAILED CALL LEAVES THE PREVIOUS ANSWER STANDING. It does not reset to
   * unresolved and it never writes a denial. `available: false` from a flaky
   * request is indistinguishable from "you are not an Agency Admin", and that
   * confusion is what cost `isAgencyAdmin` its own resolved flag.
   *
   * ⚠️ STATE IS WRITTEN ONLY WHEN SOMETHING CHANGED. This runs on the same
   * once-a-minute schedule as `revalidateScope`, and the RPC returns a fresh
   * object each time; setting it unconditionally would push a new context value
   * to every consumer every minute.
   */
  const refreshMergedScope = useCallback(async (): Promise<MergedScopeStatus | null> => {
    if (!user) {
      setMergedScope(UNRESOLVED_MERGED_SCOPE);
      mergedReadingRef.current = UNRESOLVED_MERGED_SCOPE;
      return null;
    }

    try {
      const { data, error } = await supabase.rpc('merged_scope_status');
      if (error) {
        console.error('[TenantContext] Error reading merged scope status:', error);
        return null;
      }

      const parsed = parseMergedScopeStatus(data);
      if (!parsed) {
        console.error('[TenantContext] merged_scope_status returned an unreadable payload');
        return null;
      }

      setMergedScope((prev) =>
        mergedScopeKey(prev) === mergedScopeKey(parsed) ? prev : parsed,
      );
      // SUB-4.10. The classifier reads this, and it must be the reading that
      // just arrived rather than the state React will apply later.
      mergedReadingRef.current = parsed;

      /**
       * SUB-4.7 · FR74, AC2. A scope change NOBODY IN THIS TAB MADE.
       *
       * ⚠️ THIS IS THE CASE SUB-4.1 DOES NOT COVER, AND IT IS THE ONLY ONE AC2
       * IS ABOUT. `enterMergedScope` and `switchTenant` both clear the caches
       * and then reload the page, so a deliberate change already lands on empty
       * ones. The reach can also move with no client action at all: the Agency
       * Admin grant is revoked, the anchor is archived, the last sub-account is
       * detached, or a SECOND TAB of the same user enters or leaves merged scope
       * on the one shared session row. `merged_scope_tenant_ids()` narrows on the
       * next statement; the four module-level Maps in this tree keep answering
       * for five, ten and fifteen minutes.
       *
       * That is the merged view serving single-account data and the reverse,
       * literally, and until this block it happened in silence.
       *
       * ⚠️ REACH, NOT `mergedScopeKey`. See `scopeReachKey`: keying this on the
       * render identity would empty every cache in the tree the first time an
       * offer appeared or `available` flickered, and nothing about what may be
       * read would have changed.
       *
       * ⚠️ THE FIRST RESOLUTION IS NOT A CHANGE. Going from 'unresolved' to a
       * real answer is this tab learning where it is, not the ground moving, and
       * sweeping there would throw away every query the page had just issued on
       * boot, on every load, forever.
       *
       * ⚠️ SUB-4.10 · NFR8's notice for a reach that SHRANK IS STILL NOT RAISED
       * HERE, and now that both halves exist the reason is worth stating rather
       * than inheriting. Dropping stale caches is the correctness half and it
       * has to happen on EVERY reach change, in both directions, including the
       * widening ones. Telling the user is the other half and must happen only
       * when the reach got SMALLER. Two questions, two answers, and a block that
       * did both would have to disagree with itself. `revalidateScope` raises
       * the notice, from the same reading, immediately after this returns.
       */
      const nextReach = scopeReachKey(parsed);
      const prevReach = scopeReachRef.current;
      scopeReachRef.current = nextReach;
      if (prevReach !== 'unresolved' && prevReach !== nextReach) {
        dropScopedCaches(`reach changed: ${prevReach} -> ${nextReach}`);
      }

      return parsed;
    } catch (error) {
      console.error('[TenantContext] Error reading merged scope status:', error);
      return null;
    }
  }, [user?.id, dropScopedCaches]);

  /**
   * SUB-4.1 · FR31, FR32. Enter merged scope.
   *
   * The anchor is chosen server-side and is HOME, never the current account, so
   * this works unchanged from inside a sub-account. That also means entering
   * merged scope MOVES the resolved account to the parent, exactly as switching
   * accounts does, which is why every cache is dropped here on the same terms as
   * `switchTenant`. The caller reloads; state is not patched in place.
   */
  const enterMergedScope = useCallback(async (): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase.rpc('set_scope_mode', { p_mode: 'subtree' });

    if (error) {
      console.error('[TenantContext] Error entering merged scope:', error);
      throw error;
    }

    // SUB-4.7 · AC2. Was five clears in a row; now the same sweep the UNDIRECTED
    // change takes, so a cache added by a later story is not left behind on
    // whichever of the three paths its author happened not to edit.
    // `pipelinePaginationService` registers itself, as does `permissionCache`.
    dropScopedCaches('entered merged scope');
  }, [user?.id, dropScopedCaches]);

  const fetchCurrentTenant = async () => {
    if (!user) {
      setCurrentTenant(null);
      return;
    }

    const signal = abortControllerRef.current.signal;

    try {
      const cacheKey = `current-tenant-${user.id}`;
      
      const tenant = await deduplicatedTenantRequest(cacheKey, async () => {
        return await retryWithBackoff(async () => {
          const tenantId = await getCurrentTenantId();
          
          if (!tenantId) {
            return null;
          }

          // Get tenant details
          const { data: tenantData, error: tenantError } = await supabase
            .from('tenants')
            .select('*')
            .eq('tenant_id', tenantId)
            .single();

          if (tenantError) {
            console.error('[TenantContext] Error fetching tenant details:', tenantError);
            throw tenantError;
          }

          return tenantData;
        });
      });

      if (signal.aborted) return;
      setCurrentTenant(tenant);
    } catch (error: any) {
      if (signal.aborted) return;
      console.error('[TenantContext] Error fetching current tenant:', error);
      setError(error.message);
    }
  };

  // Check if user has system.override permission
  const checkSystemOverridePermission = async (): Promise<boolean> => {
    if (!user) {
      setUserHasSystemOverride(false);
      return false;
    }

    try {
      // First check the legacy user's primary role
      const { data: userData } = await supabase
        .from('users')
        .select(`
          role_id,
          roles!inner (
            global,
            name
          )
        `)
        .eq('id', user.id)
        .single();

      // Handle the case where roles might be an array (though it should be single object)
      const userRole = Array.isArray(userData?.roles) ? userData.roles[0] : userData?.roles;
      
      let hasOverride = false;
      
      // Check legacy system first
      if (userRole?.name === 'Superadmin' || userData?.role_id === 42) {
        hasOverride = true;
      } else if (userData?.role_id) {
        // Check role_permissions for system.override in legacy system
        // Use the role's tenant_id for permission lookup
        const { data: roleData } = await supabase
          .from('roles')
          .select('global, tenant_id')
          .eq('id', userData?.role_id)
          .single();
        
        // Use role's tenant_id if available, otherwise get current tenant
        const effectiveTenantId = roleData?.tenant_id || await getCurrentTenantId();
        
        const { data: permissionCheck } = await supabase
          .from('role_permissions')
          .select(`
            permissions!inner (name)
          `)
          .eq('role_id', userData?.role_id)
          .eq('tenant_id', effectiveTenantId)
          .eq('permissions.name', 'system.override')
          .limit(1);

        hasOverride = Boolean(permissionCheck && permissionCheck.length > 0);
      }

      // If not found in legacy system, check tenant-specific user_roles
      if (!hasOverride) {
        // Check all tenant-specific roles for this user
        const { data: userRolesData } = await supabase
          .from('user_roles')
          .select(`
            role_id,
            roles!inner (
              name,
              global
            )
          `)
          .eq('user_id', user.id);

        if (userRolesData && userRolesData.length > 0) {
          // Check if any role is Superadmin or has system.override permission
          for (const userRoleData of userRolesData) {
            const role = Array.isArray(userRoleData.roles) ? userRoleData.roles[0] : userRoleData.roles;
            
            if (role?.name === 'Superadmin') {
              hasOverride = true;
              break;
            }

            // Check role_permissions for system.override
            // Use the role's tenant_id for permission lookup
            const { data: roleDetailsData } = await supabase
              .from('roles')
              .select('global, tenant_id')
              .eq('id', userRoleData.role_id)
              .single();
            
            // Use role's tenant_id if available
            const effectiveTenantId = roleDetailsData?.tenant_id;
            
            if (effectiveTenantId) {
              const { data: rolePermissionCheck } = await supabase
                .from('role_permissions')
                .select(`
                  permissions!inner (name)
                `)
                .eq('role_id', userRoleData.role_id)
                .eq('tenant_id', effectiveTenantId)
                .eq('permissions.name', 'system.override')
                .limit(1);

              if (rolePermissionCheck && rolePermissionCheck.length > 0) {
                hasOverride = true;
                break;
              }
            }
          }
        }
      }
      
      setUserHasSystemOverride(hasOverride);
      return hasOverride;
    } catch (error) {
      console.error('[TenantContext] Error checking system.override permission:', error);
      setUserHasSystemOverride(false);
      return false;
    }
  };

  const fetchUserTenants = async (hasSystemOverride?: boolean) => {
    if (!user) {
      setUserTenants([]);
      setUserIsAgencyAdmin(false);
      return;
    }

    const signal = abortControllerRef.current.signal;
    const systemOverride = hasSystemOverride !== undefined ? hasSystemOverride : userHasSystemOverride;

    try {
      // If user has system.override permission, give access to all tenants (excluding archived)
      if (systemOverride) {
        // A superadmin's picker is already unconditional, so the Agency Admin
        // marker changes nothing for them and is not worth a query. Cleared
        // rather than left, so it cannot carry over from a previous session.
        //
        // ⚠️ RESOLVED IS TRUE HERE. False is this branch's deliberate ANSWER, not
        // an unread value, and a gate waiting for the marker to resolve would
        // otherwise hang on every superadmin — which is the population the
        // /app/sub-accounts gate exists to admit.
        //
        // ⚠️ AND IT IS AN ANSWER TO A NARROWER QUESTION THAN THE FIELD'S NAME.
        // A superadmin who genuinely holds Agency Admin reads false here. Both
        // current consumers mask that with `hasSystemOverride`, which is exactly
        // why the next one will read `isAgencyAdmin` alone and be wrong. The
        // field's doc comment says so; do not treat it as "holds the role".
        setUserIsAgencyAdmin(false);
        setUserIsAgencyAdminResolved(true);
        const { data: allTenants, error: allTenantsError } = await supabase
          .from('tenants')
          .select('*')
          .or('is_archived.is.null,is_archived.eq.false')
          .order('company_name');

        if (allTenantsError) {
          console.error('[TenantContext] Error fetching all tenants for superadmin:', allTenantsError);
          return;
        }

        setUserTenants(allTenants || []);
        return;
      }

      // For non-superadmin users, resolve tenant access using legacy-first pattern:
      // PRIORITY 1: users.tenant_id (primary source of truth — every user has one)
      // PRIORITY 2: user_roles.tenant_id (for users with multi-tenant role assignments)
      const tenantIdSet = new Set<string>();

      // `role_id` rides along for the legacy half of the Agency Admin marker below.
      const { data: userRow, error: userRowError } = await supabase
        .from('users')
        .select('tenant_id, role_id')
        .eq('id', user.id)
        .single();

      if (userRowError) {
        console.error('[TenantContext] Error fetching user tenant_id:', userRowError);
      } else if (userRow?.tenant_id) {
        tenantIdSet.add(userRow.tenant_id);
      }

      // SUB-2.7 · FR12. The role name rides along on the query that was already
      // being made, so the Agency Admin marker costs no round trip. An Agency
      // Admin's rows here are their grant on the parent plus the Admin grant
      // SUB-2.6 provisions in each sub-account, which is why this one query
      // answers FR12 and FR65 together.
      const { data: userRoles, error: userRolesError } = await supabase
        .from('user_roles')
        .select('tenant_id, roles(name)')
        .eq('user_id', user.id);

      let roleRows = userRoles as { tenant_id: string | null }[] | null;

      if (userRolesError) {
        console.error('[TenantContext] Error fetching user roles:', userRolesError);

        // The embed is the only new thing in that query, and a failure to resolve
        // it would otherwise cost the user their tenant list. Losing the marker
        // hides a picker; losing the list makes real accounts unreachable, which
        // is the worse failure and the one NFR1 forbids.
        const { data: plainRoles, error: plainRolesError } = await supabase
          .from('user_roles')
          .select('tenant_id')
          .eq('user_id', user.id);

        if (plainRolesError) {
          console.error('[TenantContext] Error fetching user roles (fallback):', plainRolesError);
        }
        roleRows = plainRoles ?? null;
        // ⚠️ NOT `setUserIsAgencyAdmin(false)` AND NOTHING ELSE. That is what made
        // a failed read indistinguishable from a negative answer. The value stays
        // at its closed default, but the RESOLVED flag says the answer is unknown,
        // so a gate can decline to conclude anything from it.
        setUserIsAgencyAdmin(false);
        setUserIsAgencyAdminResolved(false);
      } else if (holdsAgencyAdminRole(userRoles)) {
        setUserIsAgencyAdmin(true);
        setUserIsAgencyAdminResolved(true);
      } else {
        // ⚠️ THE LEGACY `users.role_id` PATH, AND IT IS NOT AN EDGE CASE.
        //
        // Found 2026-08-13 while verifying SUB-3.1's entry point. This branch used
        // to be `setUserIsAgencyAdmin(false)` outright, so the marker was derived
        // from `user_roles` ALONE — and an Agency Admin whose role comes from
        // `users.role_id` read as "not an Agency Admin", losing them the picker
        // (FR12) and the /app/sub-accounts entry point (FR63) entirely.
        //
        // That is the majority path, not a corner: SUB-1.11 measured that 42 of 50
        // active users resolve their role through this column, and the live example
        // is aglasier24@gmail.com, whose `users.role_id` is Agency Admin while all
        // nine of its `user_roles` rows say Superadmin. It was invisible precisely
        // because that account is ALSO a superadmin, so `hasSystemOverride` carried
        // the gate and hid the wrong answer underneath it.
        //
        // The server has honoured both paths since 20260812j:
        // `agency_grant_basis_holds` tests them as a UNION, and SUB-3.1's
        // `is_agency_admin_of` does the same. The client honouring one was an
        // asymmetry, and the direction of it was to deny real Agency Admins.
        //
        // ⚠️ A SECOND QUERY RATHER THAN AN EMBED, DELIBERATELY. `users` reaches
        // `roles` two ways — directly through `role_id`, and through `user_roles` —
        // so `users.select('roles(name)')` risks PGRST201 "more than one
        // relationship found". `roles` SELECT is `USING (true)` for authenticated,
        // so this one always resolves. It is also short-circuited: only users whose
        // `user_roles` did NOT already answer yes pay for it.
        let legacyIsAgencyAdmin = false;
        let legacyResolved = !userRowError;

        if (!userRowError && userRow?.role_id) {
          const { data: legacyRole, error: legacyRoleError } = await supabase
            .from('roles')
            .select('name')
            .eq('id', userRow.role_id)
            .maybeSingle();

          if (legacyRoleError) {
            console.error('[TenantContext] Error resolving legacy role name:', legacyRoleError);
            legacyResolved = false;
          } else {
            legacyIsAgencyAdmin = holdsAgencyAdminRole([{ roles: legacyRole }]);
          }
        }

        setUserIsAgencyAdmin(legacyIsAgencyAdmin);
        // Same rule as the branch above: a failed read is "unknown", not "no". The
        // gate declines to conclude anything from an unresolved marker rather than
        // reading it as a denial.
        setUserIsAgencyAdminResolved(legacyResolved);
      }

      if (roleRows) {
        roleRows.forEach(ur => {
          if (ur.tenant_id) tenantIdSet.add(ur.tenant_id);
        });
      }

      if (tenantIdSet.size === 0) {
        setUserTenants([]);
        return;
      }

      const tenantIds = Array.from(tenantIdSet);

      // Fetch tenant details for all accessible tenants (excluding archived)
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('*')
        .in('tenant_id', tenantIds)
        .or('is_archived.is.null,is_archived.eq.false');

      if (tenantError) {
        console.error('[TenantContext] Error fetching tenant details:', tenantError);
        return;
      }

      setUserTenants(tenantData || []);
      
    } catch (error: any) {
      if (signal.aborted) return;
      console.error('[TenantContext] Error fetching user tenants:', error);
      // Don't set error here as it's not critical for core functionality
    }
  };

  const switchTenant = async (tenantId: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Verify user has access to this tenant
      const hasAccess = userTenants.some(tenant => tenant.tenant_id === tenantId);
      if (!hasAccess) {
        throw new Error('User does not have access to this tenant');
      }

      // Set the superadmin's active tenant session
      const { error } = await supabase
        .rpc('set_superadmin_active_tenant', { p_tenant_id: tenantId });

      if (error) {
        console.error('[TenantContext] Error setting superadmin active tenant:', error);
        throw error;
      }

      // Clear all caches to force refresh on next page load.
      // SUB-4.7 · AC2. Choosing one account also LEAVES merged scope, through
      // `set_superadmin_active_tenant`'s ON CONFLICT column list, so this path
      // is a scope change too and takes the same sweep.
      dropScopedCaches('switched account');

      // Note: We don't update state here because the page will reload immediately
      // This prevents unnecessary re-renders and refetches before the reload
    } catch (error: any) {
      console.error('[TenantContext] Error switching tenant:', error);
      throw error;
    }
  };

  const returnToHomeTenant = async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Clear the superadmin's active tenant session
      const { data, error } = await supabase
        .rpc('clear_superadmin_active_tenant');

      if (error) {
        console.error('[TenantContext] Error clearing superadmin active tenant:', error);
        throw error;
      }

      // ⚠️ SUB-4.2. THIS PATH CLEARED NOTHING. Returning home is an account change
      // exactly as much as switching into one is, and `switchTenant` and
      // `enterMergedScope` both cleared. This one called `refetch()` and left every
      // memo standing, so the caches kept answering for the account the user had
      // just left until their TTLs expired. Pre-existing, and the same shape as the
      // pipeline cache defect above.
      //
      // SUB-4.7 · AC2. That defect is the argument for one sweep rather than a list
      // of clears repeated at four call sites: it existed because ONE of the four
      // sites was missed, and the next cache added will be missed the same way.
      dropScopedCaches('returned to home account');

      // Refresh the tenant data to get the home tenant
      await refetch();
    } catch (error: any) {
      console.error('[TenantContext] Error returning to home tenant:', error);
      throw error;
    }
  };

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First check permissions, then fetch tenant data sequentially
      // Pass the permission result directly to avoid state timing issues
      const hasSystemOverride = await checkSystemOverridePermission();

      await fetchCurrentTenant();

      await fetchUserTenants(hasSystemOverride);

      // SUB-4.1. Last, because it is the only fetch here that is allowed to
      // fail without costing the user their account list.
      await refreshMergedScope();
    } catch (error: any) {
      console.error('[TenantContext] Error in refetch:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const addTenant = useCallback((tenant: Tenant) => {
    // Add the new tenant to the list and sort alphabetically
    setUserTenants(prev => {
      const exists = prev.some(t => t.tenant_id === tenant.tenant_id);
      if (exists) return prev;

      const updated = [...prev, tenant];
      return updated.sort((a, b) => a.company_name.localeCompare(b.company_name));
    });
  }, []);

  // Get default tenant for unauthenticated domain-based detection
  const getDefaultTenantForDomain = async () => {
    try {
      const domainTenant = await getDomainTenant();
      if (domainTenant) {
        // Convert domain tenant to our Tenant interface
        return {
          tenant_id: domainTenant.tenantId,
          company_name: domainTenant.tenantName,
          contact_name: '',
          contact_phone: '',
          contact_email: '',
          created_at: '',
          updated_at: ''
        } as Tenant;
      }
    } catch (error) {
      console.error('Error getting domain tenant:', error);
    }
    
    // Fallback to default tenant
    const defaultTenant = getDefaultTenant();
    return {
      tenant_id: defaultTenant.tenantId,
      company_name: defaultTenant.tenantName,
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      created_at: '',
      updated_at: ''
    } as Tenant;
  };

  // Fetch tenant data when user changes
  useEffect(() => {
    if (user && !authLoading) {
      refetch();
    } else if (!user && !authLoading) {
      // Only run domain-based tenant lookup for truly unauthenticated users
      // Don't run if auth is still loading (prevents tab switch triggers)
      setIsLoading(true);
      getDefaultTenantForDomain().then(domainTenant => {
        setCurrentTenant(domainTenant);
        setUserTenants([]);
        // There is no signed-in user, so "holds no Agency Admin role" is a real
        // answer rather than an unread one. Without this, a gate that requires
        // the marker to be resolved would wait forever on a public page.
        setUserIsAgencyAdmin(false);
        setUserIsAgencyAdminResolved(true);
        // SUB-4.1. There is no signed-in user, so "no merged scope" is a real
        // answer. Resolved, so a gate waiting on it does not hang on a public
        // page — the same reasoning as the Agency Admin marker above.
        setMergedScope({ ...UNRESOLVED_MERGED_SCOPE, resolved: true });
        setIsLoading(false);
        setError(null);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  /**
   * SUB-3.12 · NFR8. When to ask.
   *
   * ⚠️ THE TIMER IS THE FLOOR, NOT THE MECHANISM. A revocation lands server-side
   * at an arbitrary moment and nothing pushes it to an open tab, so the only
   * honest options are polling or waiting for an interaction. Focus and
   * visibility cover the common shape by a wide margin (the tab was left open,
   * something changed, the user came back to it); the timer covers the tab that
   * is watched and never touched. Sixty seconds is one indexed RPC per minute
   * per open tab, against a function that is already evaluated inside hundreds
   * of policies on every page load.
   *
   * ⚠️ ONLY WHILE VISIBLE. A background tab that polls is a background tab that
   * raises a blocking notice nobody is looking at, and then holds it there.
   */
  /**
   * RBAC-4.3. A notice is about a signed-in session; it must not outlive it. When someone's access is
   * removed, this poll sees their account resolve to nothing and raises "could not be determined", and
   * a moment later they are signed out. The poll below stops at sign-out, so nothing cleared the
   * notice: its modal sat over the login page, hiding the form and the deactivated sentence (found
   * driving RBAC-4.3, 2026-11-02).
   */
  useEffect(() => {
    if (!user && !authLoading) {
      setScopeNotice(null);
      setScopeStatus('unknown');
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (!user || authLoading) return;

    let cancelled = false;
    /**
     * SUB-4.1. Merged scope is revalidated server-side on every statement, so
     * the picker must not be the one surface still offering a scope the database
     * has already stopped granting. Same schedule, one more indexed RPC per
     * minute per visible tab.
     *
     * ⚠️ SUB-4.10 · SEQUENCED, NOT FIRED IN PARALLEL, and that is the whole
     * wiring of this story. The merged reading is what turns "the resolver
     * answers one account" into "the set you asked for is no longer being
     * served", so it has to be IN HAND before the classification runs. Issued
     * side by side, the first poll after a degradation classifies against last
     * minute's reach and reports agreement, and the notice arrives a minute late
     * or, if the two land in the other order, never.
     *
     * Two round trips in series once a minute, against functions already
     * evaluated inside hundreds of policies on every page load.
     */
    const ask = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      const merged = await refreshMergedScope();
      if (cancelled) return;
      await revalidateScope(merged);
    };

    // Once as soon as the tenant is known, so a tab restored from the bfcache
    // does not run on a stale account until the first interval elapses.
    //
    // ⚠️ SUB-4.10. This first run is also the ONLY thing that catches a scope
    // that had already stopped being honoured before the page loaded, and it
    // catches it because `requested_scope_mode` survives the reload where a
    // previous reading cannot.
    void ask();

    const onFocus = () => { void ask(); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void ask();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(() => { void ask(); }, 60_000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
  }, [user?.id, authLoading, currentTenant?.tenant_id, revalidateScope, refreshMergedScope]);

  /**
   * SUB-4.10 · NFR8. What the notice's one button does.
   *
   * ⚠️ WITHOUT THE `set_scope_mode` CALL THIS NOTICE IS A TRAP, and it is the
   * defect the intent-based half of the story creates. `requested_scope_mode` is
   * the STORED column and it survives a reload by design — that is the entire
   * reason it exists. So a session whose merged scope stopped being honoured
   * raises the notice, the user reloads, the column still says `subtree`, the
   * server still serves one account, and the modal comes back. Forever, on every
   * load, with no way through it, for a user who may simply have left an old
   * session row behind.
   *
   * Acknowledging therefore puts the session into the state it is ACTUALLY being
   * served in. That is not a decision taken away from the user: leaving merged
   * scope is always allowed, needs no gate, and the picker still offers All
   * Accounts on the next click if they still qualify (`available` is a separate
   * question and Part C of the suite measures it staying true).
   *
   * ⚠️ ONLY FOR THE FELL-OUT CASE. A still-merged shrink (three accounts to two)
   * leaves a stored intent that is being honoured, and normalizing it there
   * would drop the user out of a merged scope they still have.
   *
   * ⚠️ THE RELOAD HAPPENS WHETHER OR NOT THE RPC DOES. Blocking the one exit
   * from a modal on a network call is how a notice about stale data becomes an
   * unusable application.
   */
  const acknowledgeScopeNotice = useCallback(async (): Promise<void> => {
    const reading = mergedReadingRef.current;
    const fellOutOfMergedScope =
      scopeNotice?.status === 'degraded' &&
      reading.requestedScopeMode === 'subtree' &&
      reading.tenantIds.length <= 1;

    if (fellOutOfMergedScope) {
      try {
        await supabase.rpc('set_scope_mode', { p_mode: 'single' });
      } catch (error) {
        console.error('[TenantContext] Could not normalise the stored scope mode:', error);
      }
    }

    window.location.reload();
  }, [scopeNotice?.status]);

  const value: TenantContextType = useMemo(() => ({
    currentTenant,
    userTenants,
    isAgencyAdmin: userIsAgencyAdmin,
    // SUB-2.90 · F-82. Published so it can be paired with `isAgencyAdmin` above,
    // which was cleared on the strength of exactly this value.
    hasSystemOverride: userHasSystemOverride,
    isAgencyAdminResolved: userIsAgencyAdminResolved,
    switchTenant,
    returnToHomeTenant,
    getCurrentTenantId,
    scopeStatus,
    revalidateScope,
    mergedScope,
    enterMergedScope,
    isLoading,
    error,
    refetch,
    addTenant
  }), [currentTenant, userTenants, userIsAgencyAdmin, userHasSystemOverride, userIsAgencyAdminResolved, switchTenant, returnToHomeTenant, getCurrentTenantId, scopeStatus, revalidateScope, mergedScope, enterMergedScope, isLoading, error, refetch, addTenant]);

  return (
    <TenantContext.Provider value={value}>
      {children}
      {/*
        SUB-3.12 · NFR8. Rendered HERE rather than mounted in a layout, so it
        cannot be missing from the one layout somebody is looking at. It renders
        nothing at all until `scopeNotice` is non-null.
      */}
      <ScopeChangeNotice notice={scopeNotice} onReload={acknowledgeScopeNotice} />
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
